import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../config/api';

const FEATURES = [
  {
    title: 'Test every feature',
    body: 'New lighting modes, schedules, and app updates land with beta testers first. Use them on your real devices and tell us what feels right.',
  },
  {
    title: 'Get firmware early',
    body: 'Beta devices receive updates ahead of the production release, so you can try fixes and new capabilities as soon as they are ready.',
  },
  {
    title: 'Help shape Fluora',
    body: 'Your feedback goes straight to the team. The more you try — and the more you report — the better every Fluora gets.',
  },
];

const STEPS = [
  {
    title: 'Create an account',
    body: 'Sign up with email or Google. This is a regular Fluora account until you opt in.',
  },
  {
    title: 'Join the program',
    body: 'After you sign in, tap Join Beta. We will mark your account and move every device registered to you onto the beta track.',
  },
  {
    title: 'Use Fluora as usual',
    body: 'Keep using the mobile app. Try the new features, and let us know what works and what does not.',
  },
];

const FULL_BANNER =
  'We are at max. Please try again at a later time when a seat might become available.';

const BetaLandingPage: React.FC = () => {
  const [spots, setSpots] = useState<{ available: number; max: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/beta/spots`)
      .then((response) => {
        if (cancelled) return;
        const available = Number(response.data?.available);
        const max = Number(response.data?.max);
        if (Number.isInteger(available) && Number.isInteger(max) && max >= 0) {
          setSpots({ available: Math.max(0, available), max });
        }
      })
      .catch(() => {
        if (!cancelled) setSpots(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isFull = Boolean(spots && spots.available <= 0);

  return (
    <main className="beta-landing">
      {isFull && (
        <div className="beta-landing-full-banner" role="status">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          {FULL_BANNER}
        </div>
      )}
      <section className="beta-landing-hero">
        <span className="beta-portal-badge">Beta Program</span>
        <h1>Welcome to Fluora Beta</h1>
        <p className="beta-landing-lead">
          Try new firmware and app features before anyone else — and help us make
          Fluora better for every grower.
        </p>
        {spots && (
          <div className="beta-landing-spots" role="status">
            {spots.available} {spots.available === 1 ? 'spot' : 'spots'} of {spots.max} available
          </div>
        )}
      </section>

      <section className="beta-landing-features" aria-label="Why join">
        {FEATURES.map((feature) => (
          <article key={feature.title} className="beta-landing-card">
            <h2>{feature.title}</h2>
            <p>{feature.body}</p>
          </article>
        ))}
      </section>

      <section className="beta-landing-how" aria-labelledby="beta-how-heading">
        <h2 id="beta-how-heading">How it works</h2>
        <ol className="beta-landing-steps">
          {STEPS.map((step, index) => (
            <li key={step.title}>
              <span className="beta-landing-step-num">{index + 1}</span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="beta-landing-cta">
        <h2>Ready to try the next Fluora?</h2>
        <p>
          Create an account to get started. You will opt into the beta after you
          sign in.
        </p>
        {isFull ? (
          <button type="button" className="btn beta-landing-cta-btn" disabled>
            Join the Beta Program!
          </button>
        ) : (
          <Link to="/register" className="btn beta-landing-cta-btn">
            Join the Beta Program!
          </Link>
        )}
      </section>
    </main>
  );
};

export default BetaLandingPage;
